import React, { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import {
  InputGroup,
  FieldWrappers,
  Label,
  SubmitBtn,
  SideCancelBtn,
  Wrapper,
  ScrollableContent,
  ActionSection,
  MobileIcon,
  ModalHeader,
} from "../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import * as Yup from "yup";
import "react-datepicker/dist/react-datepicker.css";

// Step 1 Validation Schema
const stepOneSchema = Yup.object().shape({
  brokerName: Yup.string()
    .matches(
      /^[A-Za-z0-9. - ]+$/,
      "Only alphabets, numbers, spaces and hyphens are allowed",
    )
    .min(3, "Broker name must be at least 3 characters long")
    .required("Broker name is required"),
  mobile: Yup.string()
    .matches(/^\d{10}$/, "Mobile number must be exactly 10 digits")
    .required("Mobile number is required"),
  email: Yup.string()
    .matches(
      /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
      "Enter a valid email address",
    )
    .nullable()
    .notRequired()
    .transform((value) => (value === "" ? null : value)),
  firmName: Yup.string().nullable(),
  reraNum: Yup.string()
    .min(6, "Too short")
    .max(40, "Too long")
    .nullable(),
  address: Yup.string().nullable(),
});

const AddBroker = ({
  addBrokerMutate,
  addBrokerLoading,
  handleClose,
  selectedData,
}) => {
  const [formData, setFormData] = useState({
    brokerName: "",
    mobile: "",
    email: "",
    address: "",
    firmName: "",
    reraNum: "",
  });

  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleSubmit = (values) => {
    addBrokerMutate({
      name: values.brokerName,
      mobile: values.mobile,
      email: values.email,
      firmName: values.firmName || "",
      reraNum: values.reraNum || "",
      address: values.address || "",
    });
  };

  return (
    <Container
      style={{
        height: `${height}px`,
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <ModalHeader>
        <Title>{"Add Broker"}</Title>
        <button onClick={handleClose}>
          <MdClose color="white" size={24} />
        </button>
      </ModalHeader>
      <div className="px-6 h-full flex flex-col justify-between">
        <Formik
          initialValues={formData}
          onSubmit={handleSubmit}
          validationSchema={stepOneSchema}
          enableReinitialize
        >
          {({
            values,
            setFieldValue,
            errors,
            touched,
            handleChange,
            handleBlur,
          }) => (
            <Form>
              <ScrollableContent className="max-h-[calc(100dvh-150px)]">
                <Wrapper>
                  <InputGroup>
                    <Label htmlFor="brokerName">Broker Name *</Label>
                    <FieldWrappers>
                      <Field
                        type="text"
                        name="brokerName"
                        id="brokerName"
                        autoComplete="off"
                        placeholder="Enter broker name"
                      />
                    </FieldWrappers>
                    {errors.brokerName && touched.brokerName && (
                      <ErrorMessage>{errors.brokerName}</ErrorMessage>
                    )}
                  </InputGroup>

                  {/* Mobile Number */}
                  <InputGroup>
                    <Label htmlFor="mobile">Mobile Number *</Label>
                    <FieldWrappers>
                      <MobileIcon />
                      <Field
                        type="text"
                        name="mobile"
                        id="mobile"
                        autoComplete="off"
                        maxLength={10}
                        placeholder="Enter 10-digit mobile number"
                        className="w-full pl-5"
                      />
                    </FieldWrappers>
                    {errors.mobile && touched.mobile && (
                      <ErrorMessage>{errors.mobile}</ErrorMessage>
                    )}
                  </InputGroup>

                  <InputGroup>
                    <Label htmlFor="email">Email</Label>
                    <FieldWrappers>
                      <Field
                        type="email"
                        name="email"
                        id="email"
                        autoComplete="off"
                        placeholder="Enter email address (optional)"
                      />
                    </FieldWrappers>
                    {errors.email && touched.email && (
                      <ErrorMessage>{errors.email}</ErrorMessage>
                    )}
                  </InputGroup>

                  <InputGroup>
                    <Label htmlForm="firmName">Firm/Company Name</Label>
                    <FieldWrappers>
                      <Field
                        type="text"
                        name="firmName"
                        id="firmName"
                        autoComplete="off"
                        placeholder="Enter firm name (optional)"
                      />
                    </FieldWrappers>
                    {errors.firmName && touched.firmName && (
                      <ErrorMessage>{errors.firmName}</ErrorMessage>
                    )}
                  </InputGroup>

                  <InputGroup>
                    <Label htmlFor="reraNum">RERA NUmber</Label>
                    <FieldWrappers>
                      <Field
                        type="text"
                        name="reraNum"
                        id="reraNum"
                        autoComplete="off"
                        placeholder="Enter RERA number (optional)"
                      />
                    </FieldWrappers>
                    {errors.reraNum && touched.reraNum && (
                      <ErrorMessage>{errors.reraNum}</ErrorMessage>
                    )}
                  </InputGroup>

                  <InputGroup>
                    <Label htmlFor="address">Address</Label>
                    <FieldWrappers>
                      <Field
                        type="text"
                        name="address"
                        id="address"
                        autoComplete="off"
                        placeholder="Enter address (optional)"
                      />
                    </FieldWrappers>
                    {errors.address && touched.address && (
                      <ErrorMessage>{errors.address}</ErrorMessage>
                    )}
                  </InputGroup>
                </Wrapper>
              </ScrollableContent>
              <ActionSection>
                <SideCancelBtn type="button" onClick={handleClose}>
                  Cancel
                </SideCancelBtn>
                <SubmitBtn type="submit">
                  {addBrokerLoading ? "Submiting..." : "Submit"}
                </SubmitBtn>
              </ActionSection>
            </Form>
          )}
        </Formik>
      </div>
    </Container>
  );
};

// Styled components
const Header = tw.div`flex items-center justify-between bg-[#26A69A] py-4 px-4`;
const Container = tw.div`bg-white w-full h-full min-h-[100vh] flex flex-col`;
const Title = tw.h1`text-white text-lg font-medium`;
const ErrorMessage = tw.div`text-red-500 text-xs mt-1`;

export default AddBroker;
